import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2, ArrowRight } from "lucide-react";
import api from "@/api/client";

/**
 * Landing page after the customer returns from MyPay's hosted payment page
 * for a DIRECT ORDER payment (not wallet top-up). The order lives in
 * `pending_payment` status until the webhook lands and flips it to
 * `pending`. This page polls the intent status and, once resolved,
 * either redirects to /track/<order_id> (success) or /orders (failure).
 */
export default function MyPayOrderReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const custom = params.get("custom");
  const cancelled = params.get("cancel") === "1";
  const [state, setState] = useState({ status: "loading", order: null });
  const [tries, setTries] = useState(0);

  useEffect(() => {
    if (!custom) { navigate("/orders", { replace: true }); return; }
    let stopped = false;
    let tick = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/mypay/order/${custom}/status`);
        if (stopped) return;
        setState({ status: data.status, order: data.order, credited: data.credited });
        setTries((n) => n + 1);
        if (data.credited || ["completed", "failed", "cancelled"].includes(String(data.status || "").toLowerCase())) {
          return; // final state — user chooses next step below
        }
      } catch { /* keep polling */ }
      tick += 1;
      if (tick > 45) { setState((s) => ({ ...s, status: "timeout" })); return; }
      setTimeout(poll, 1000);
    };
    poll();
    return () => { stopped = true; };
  }, [custom, navigate]);

  const succeeded = state.credited || state.status === "completed";
  const failed = cancelled || ["failed", "cancelled", "timeout"].includes(String(state.status || "").toLowerCase());

  return (
    <div className="min-h-screen bg-[var(--surface-alt)] flex items-center justify-center p-4" data-testid="mypay-order-return-page">
      <div className="max-w-md w-full bg-white rounded-3xl talabya-shadow-float p-6 text-center">
        {!succeeded && !failed && (
          <>
            <div className="w-16 h-16 rounded-full bg-primary-light text-primary-brand flex items-center justify-center mx-auto mb-3">
              <Loader2 size={32} className="animate-spin" />
            </div>
            <h2 className="text-lg font-black" style={{ fontFamily: "Cairo" }}>ننتظر تأكيد الدفع</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5 mb-4">قد يستغرق ذلك حتى دقيقة. لا تغلق الصفحة.</p>
            <div className="text-xs text-[var(--text-secondary)]">({tries})</div>
          </>
        )}

        {succeeded && (
          <>
            <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 size={40} />
            </div>
            <h2 className="text-lg font-black text-emerald-700" style={{ fontFamily: "Cairo" }} data-testid="mypay-order-success">تم الدفع بنجاح</h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5 mb-5">
              طلبك <b>{state.order?.code}</b> وصل إلى المطعم وهو الآن قيد التحضير.
            </p>
            <button
              onClick={() => state.order?.id ? navigate(`/track/${state.order.id}`) : navigate("/orders")}
              className="w-full py-3 rounded-2xl bg-primary-brand hover:bg-blue-800 text-white font-black flex items-center justify-center gap-2"
              data-testid="mypay-order-track-btn"
            >
              تتبع الطلب <ArrowRight size={16} className="rotate-180"/>
            </button>
          </>
        )}

        {failed && !succeeded && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-50 text-red-600 flex items-center justify-center mx-auto mb-3">
              <XCircle size={40} />
            </div>
            <h2 className="text-lg font-black text-red-700" style={{ fontFamily: "Cairo" }} data-testid="mypay-order-failed">
              {cancelled ? "تم إلغاء عملية الدفع" : "لم تنجح عملية الدفع"}
            </h2>
            <p className="text-sm text-[var(--text-secondary)] mt-1.5 mb-5">لم يتم إرسال الطلب — لم يخصم أي مبلغ. يمكنك إعادة المحاولة.</p>
            <button
              onClick={() => navigate("/orders")}
              className="w-full py-3 rounded-2xl bg-primary-brand hover:bg-blue-800 text-white font-black"
              data-testid="mypay-order-back-btn"
            >
              العودة إلى طلباتي
            </button>
          </>
        )}
      </div>
    </div>
  );
}
